import { ErrorBoundary } from 'react-error-boundary';

import { AppProvider } from '@/app';
import { AppLayout } from '@/layouts/AppLayout';
import { ErrorBoundaryFallback } from '@/components/ErrorBoundaryFallback';
import { sentryCreateBrowserRouter } from '@/sentry';
import { APP_LINKS } from './helpers/links';
import SignUpPage from './pages/SignUpPage';
import SignInPage from './pages/SignInPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import RecoverPasswordPage from './pages/RecoverPasswordPage';
import DevPage from './pages/DevPage';
import DashboardPage from './pages/DashboardPage';
import PageNotFound from './pages/PageNotFound';
import FundraisingPage from './pages/FundraisingPage';
import PaymentsPage from './pages/PaymentsPage';
import PaymentsNextPage from './pages/PaymentsNextPage';
// import PaymentsPrepayPage from './pages/PaymentsPrepayPage';
import PaymentsBankDetailsPage from './pages/PaymentsBankDetailsPage';
import PaymentsAddFundsPage from './pages/PaymentsAddFundsPage';
import PaymentsAddFundsResultPage from './pages/PaymentsAddFundsResultPage';
import PaymentsWithdrawPage from './pages/PaymentsWithdrawPage';
import FoundersPage from './pages/FoundersPage';
import FounderDetailPage from './pages/FounderDetailPage';
import LoansPage from './pages/LoansPage';
import ActivityPage from './pages/ActivityPage';
import ProfilePage from './pages/ProfilePage';
import HelpPage from './pages/HelpPage';
import LoanTermsPage from './pages/LoanTermsPage';
import LoanTermsPlansPage from './pages/LoanTermsPlansPage';
import LoanTermsFpgPage from './pages/LoanTermsFpgPage';
import SwapGuaranteePage from './pages/SwapGuaranteePage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import BrandsPage from './pages/BrandsPage';
import BrandDetailPage from './pages/BrandDetailPage';
import RefinancingPage from './pages/RefinancingPage';
import PersonalGuaranteePage from './pages/PersonalGuaranteePage';
import ReportingPage from './pages/ReportingPage';
import ReportingUploadPage from './pages/ReportingUploadPage';
import ReportingModesPage from './pages/ReportingModesPage';
import CashMovementsPage from './pages/CashMovementsPage';
import PortfolioMovementsPage from './pages/PortfolioMovementsPage';
import TaxReportPage from './pages/TaxReportPage';
import LoanTermsFlexibilityPage from './pages/LoanTermsFlexibilityPage';
import FundraisingApplicationUploadPage from './pages/FundraisingApplicationUploadPage';

const Routes = [
  {
    element: (
      <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
        <AppProvider />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/sign-up/co-founder/:inviteCode',
        element: <SignUpPage />,
      },
      {
        path: '/sign-up/:invite',
        element: <SignUpPage />,
      },
      {
        path: '/sign-in',
        element: <SignInPage />,
      },
      {
        path: APP_LINKS.reportingUpload(':id'),
        element: <ReportingUploadPage />,
      },
      {
        path: APP_LINKS.fundraisingApplication,
        element: <FundraisingApplicationUploadPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/password/set/:key',
        element: <RecoverPasswordPage />,
      },
      {
        path: '/legal/terms-of-use/',
        element: <TermsOfUsePage />,
      },
      {
        path: APP_LINKS.root,
        element: <AppLayout />,
        children: [
          {
            path: APP_LINKS.dashboard,
            element: <DashboardPage />,
          },
          {
            path: APP_LINKS.fundraising,
            element: <FundraisingPage />,
          },
          {
            path: APP_LINKS.founders,
            element: <FoundersPage />,
          },
          {
            path: APP_LINKS.brands,
            element: <BrandsPage />,
          },
          {
            path: APP_LINKS.startupDetail(':slug'),
            element: <BrandDetailPage />,
          },
          {
            path: APP_LINKS.founderDetail(':id'),
            element: <FounderDetailPage />,
          },
          {
            path: APP_LINKS.activity,
            element: <ActivityPage />,
          },
          {
            path: APP_LINKS.profile,
            element: <ProfilePage />,
          },
          {
            path: APP_LINKS.activity,
            element: <ActivityPage />,
            children: [
              {
                path: APP_LINKS.cashMovements,
                element: <CashMovementsPage />,
              },
              {
                path: APP_LINKS.portfolioMovements,
                element: <PortfolioMovementsPage />,
              },
              {
                path: APP_LINKS.taxReport,
                element: <TaxReportPage />,
              },
            ],
          },
          {
            path: '/payments',
            element: <PaymentsPage />,
            children: [
              {
                path: APP_LINKS.nextPayment,
                element: <PaymentsNextPage />,
              },
              {
                path: APP_LINKS.bankAccounts + '/*',
                element: <PaymentsBankDetailsPage />,
              },
              // {
              //   path: '/payments/prepay-loans',
              //   element: PaymentsPrepayPage,
              // },
              {
                path: APP_LINKS.addFunds,
                element: <PaymentsAddFundsPage />,
              },
              {
                path: APP_LINKS.addFundsResult,
                element: <PaymentsAddFundsResultPage />,
              },
              {
                path: APP_LINKS.withdraw,
                element: <PaymentsWithdrawPage />,
              }
            ],
          },
          {
            path: APP_LINKS.loans,
            element: <LoansPage />,
          },
          {
            path: APP_LINKS.reporting,
            element: <ReportingPage />,
          },
          {
            path: APP_LINKS.fpg,
            element: <PersonalGuaranteePage />,
          },
          {
            path: APP_LINKS.loanTerms,
            element: <LoanTermsPage />,
            children: [
              {
                path: APP_LINKS.loanTermsPlans(),
                element: <LoanTermsPlansPage />,
              },
              {
                path: APP_LINKS.loanTermsPlans(':version'),
                element: <LoanTermsPlansPage />,
              },
              {
                path: APP_LINKS.loanTermsFpg(),
                element: <LoanTermsFpgPage />,
              },
              {
                path: APP_LINKS.loanTermsFpg(':version'),
                element: <LoanTermsFpgPage />,
              },
              {
                path: APP_LINKS.loanTermsRefinancing(),
                element: <RefinancingPage />,
              },
              {
                path: APP_LINKS.loanTermsRefinancing(':version'),
                element: <RefinancingPage />,
              },
              {
                path: APP_LINKS.loanTermsFlexibility(),
                element: <LoanTermsFlexibilityPage />,
              },
              {
                path: APP_LINKS.loanTermsFlexibility(':version'),
                element: <LoanTermsFlexibilityPage />,
              },
              {
                path: APP_LINKS.loanTermsReportingModes(),
                element: <ReportingModesPage />,
              },
              {
                path: APP_LINKS.loanTermsReportingModes(':version'),
                element: <ReportingModesPage />,
              },
            ],
          },
          {
            path: '/help/swap-guarantees',
            element: <SwapGuaranteePage />,
          },
          {
            path: APP_LINKS.help,
            element: <HelpPage />,
          },
        ],
      },
      {
        path: '/dev',
        element: <DevPage />,
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],
  },
];

export const AppRoutes = sentryCreateBrowserRouter(Routes);
